<template>
  <transition name="modal-fade">
    <div v-if="isOpen" class="pdf-modal" @click.self="close">
      <div class="modal-container">
        <div class="modal-header">
          <h3 class="modal-title">{{ title }}</h3>
          <div class="modal-header-actions">
            <a
              v-if="externalLink"
              :href="externalLink"
              target="_blank"
              rel="noopener noreferrer"
              class="external-link-btn"
              title="Abrir en el sitio original"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/>
                <polyline points="15 3 21 3 21 9"/>
                <line x1="10" y1="14" x2="21" y2="3"/>
              </svg>
              <span>Abrir original</span>
            </a>
            <button @click="close" class="close-btn" title="Cerrar (Esc)">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <line x1="18" y1="6" x2="6" y2="18"/>
                <line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
          </div>
        </div>

        <div class="pdf-wrapper" ref="pdfScroll">
          <div v-if="pdfLoading" class="pdf-status">
            <div class="pdf-spinner"></div>
            <span>Cargando PDF…</span>
          </div>

          <div v-else-if="pdfError" class="pdf-status pdf-status--error">
            <span>No se pudo cargar el PDF en el sitio.</span>
            <a v-if="externalLink" :href="externalLink" target="_blank" rel="noopener noreferrer" class="pdf-status-link">
              Abrir en el sitio original
            </a>
          </div>

          <div v-else class="pdf-pages">
            <canvas
              v-for="n in numPages"
              :key="n"
              ref="pageCanvas"
              class="pdf-page-canvas"
            ></canvas>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
let pdfjsLibPromise = null;
function loadPdfjs() {
  if (!pdfjsLibPromise) {
    pdfjsLibPromise = import('pdfjs-dist/legacy/build/pdf').then(lib => {
      lib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';
      return lib;
    });
  }
  return pdfjsLibPromise;
}

// Intenta cargar el PDF directo; si el origen no tiene CORS habilitado,
// reintenta a través de nuestra propia Netlify Edge Function (netlify/edge-functions/pdf-proxy.js).
async function loadPdfDocument(pdfjsLib, url) {
  try {
    return await pdfjsLib.getDocument(url).promise;
  } catch (directError) {
    try {
      const proxyUrl = `/api/pdf-proxy?url=${encodeURIComponent(url)}`;
      return await pdfjsLib.getDocument(proxyUrl).promise;
    } catch (proxyError) {
      throw directError;
    }
  }
}

export default {
  name: 'PdfViewerModal',
  data() {
    return {
      isOpen: false,
      externalLink: '',
      title: '',
      numPages: 0,
      pdfLoading: false,
      pdfError: false,
      renderToken: 0,
      pdfDoc: null
    };
  },
  methods: {
    async open(item) {
      this.close(); // limpia cualquier render/documento previo

      const myToken = ++this.renderToken;

      this.externalLink = item.link || '';
      this.title = item.title || '';
      this.isOpen = true;
      document.body.style.overflow = 'hidden';

      if (!item.linkPdf) {
        this.pdfError = true;
        return;
      }

      this.pdfLoading = true;

      try {
        const pdfjsLib = await loadPdfjs();
        const pdf = await loadPdfDocument(pdfjsLib, item.linkPdf);
        if (myToken !== this.renderToken) {
          pdf.destroy();
          return;
        }

        this.pdfDoc = pdf;
        this.numPages = pdf.numPages;
        this.pdfLoading = false;

        await this.$nextTick();
        await this.renderPages(pdf, myToken);
      } catch (e) {
        console.error('Error al cargar el PDF:', e);
        if (myToken === this.renderToken) {
          this.pdfLoading = false;
          this.pdfError = true;
        }
      }
    },

    async renderPages(pdf, token) {
      if (!this.$refs.pdfScroll) return;

      const containerWidth = this.$refs.pdfScroll.clientWidth - 32;
      const dpr = window.devicePixelRatio || 1;

      for (let i = 1; i <= pdf.numPages; i++) {
        if (token !== this.renderToken) return;

        const canvas = this.$refs.pageCanvas && this.$refs.pageCanvas[i - 1];
        if (!canvas) continue;

        const page = await pdf.getPage(i);
        const unscaledViewport = page.getViewport({ scale: 1 });
        const scale = containerWidth / unscaledViewport.width;
        const viewport = page.getViewport({ scale });

        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const ctx = canvas.getContext('2d');
        ctx.scale(dpr, dpr);

        if (token !== this.renderToken) return;
        await page.render({ canvasContext: ctx, viewport }).promise;
      }
    },

    close() {
      this.renderToken++; // invalida cualquier render en curso
      this.isOpen = false;
      this.numPages = 0;
      this.pdfLoading = false;
      this.pdfError = false;
      this.externalLink = '';
      this.title = '';
      document.body.style.overflow = '';

      if (this.pdfDoc) {
        this.pdfDoc.destroy();
        this.pdfDoc = null;
      }
    },
    handleEscKey(event) {
      if (event.key === 'Escape' && this.isOpen) {
        this.close();
      }
    }
  },
  mounted() {
    window.addEventListener('keydown', this.handleEscKey);
  },
  beforeUnmount() {
    window.removeEventListener('keydown', this.handleEscKey);
    document.body.style.overflow = '';
    if (this.pdfDoc) {
      this.pdfDoc.destroy();
      this.pdfDoc = null;
    }
  }
};
</script>

<style scoped>
.pdf-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.85);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 900px;
  height: calc(100vh - 40px);
  height: calc(100dvh - 40px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
}

.modal-title {
  color: white;
  font-size: 16px;
  font-weight: 700;
  margin: 0;
  flex: 1;
  line-height: 1.3;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.modal-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.external-link-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  height: 40px;
  padding: 0 14px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  font-size: 13px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.external-link-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.close-btn {
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  transform: scale(1.05);
}

.pdf-wrapper {
  flex: 1;
  min-height: 0;
  background: #2d333b;
  border-radius: 12px;
  overflow-y: auto;
  overflow-x: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.8);
  -webkit-overflow-scrolling: touch;
  padding: 16px;
}

.pdf-pages {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.pdf-page-canvas {
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.5);
  border-radius: 4px;
  max-width: 100%;
}

.pdf-status {
  height: 100%;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: #a0aec0;
  font-size: 14px;
  font-weight: 600;
  text-align: center;
  padding: 20px;
}

.pdf-status--error {
  color: #fca5a5;
}

.pdf-status-link {
  color: #4a9eff;
  font-weight: 600;
  text-decoration: underline;
}

.pdf-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid rgba(255, 255, 255, 0.15);
  border-top-color: #4a9eff;
  border-radius: 50%;
  animation: pdf-spin 0.8s linear infinite;
}

@keyframes pdf-spin {
  to { transform: rotate(360deg); }
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.3s ease;
}

.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}

@media (max-width: 768px) {
  .pdf-modal {
    padding: 10px;
  }

  .modal-container {
    height: calc(100vh - 20px);
    height: calc(100dvh - 20px);
  }

  .modal-title {
    font-size: 14px;
  }

  .external-link-btn span {
    display: none;
  }

  .external-link-btn {
    width: 40px;
    padding: 0;
    justify-content: center;
  }

  .pdf-wrapper {
    padding: 10px;
  }
}
</style>
